import type {
  CellClassParams,
  CellStyle,
  ColDef,
  ColumnState,
  ValueFormatterParams,
} from 'ag-grid-community';
import {
  emptyCustomization,
  type ColumnCustomization,
  type UserCustomizationSettings,
} from '../types/UserCustomization';
import { applyFormat } from './customizationFormatters';

// ---------- Public API ----------

/**
 * Merge user customization into the page's base ColDefs.
 *
 *   1. Reorder array per `grid.columnOrder` (fields not in the order stay at end).
 *   2. For each column with a customization, layer styling on top of the existing
 *      ColDef (cellStyle / valueFormatter / cellClass). The existing ColDef's
 *      cellEditor, valueSetter, valueGetter, editable, cellDataType, etc. are
 *      preserved — we only ADD presentation.
 *
 * Returns NEW ColDef objects (does not mutate baseColumnDefs).
 */
export function applyCustomization<T>(
  baseColumnDefs: ColDef<T>[],
  settings: UserCustomizationSettings | null,
): ColDef<T>[] {
  if (!settings) return baseColumnDefs;

  // Reorder.
  const order = settings.grid.columnOrder ?? [];
  const indexByField = new Map<string, number>();
  for (let i = 0; i < order.length; i++) indexByField.set(order[i], i);

  const sorted = [...baseColumnDefs].sort((a, b) => {
    const af = (a.field as string | undefined) ?? '';
    const bf = (b.field as string | undefined) ?? '';
    const ai = indexByField.has(af) ? indexByField.get(af)! : Number.MAX_SAFE_INTEGER;
    const bi = indexByField.has(bf) ? indexByField.get(bf)! : Number.MAX_SAFE_INTEGER;
    return ai - bi;
  });

  // Per-column layering.
  return sorted.map((col) => {
    const field = col.field as string | undefined;
    if (!field) return col;
    const colCust = settings.columns[field];
    if (!colCust) return col;
    return layerColumnCustomization(col, colCust);
  });
}

/**
 * Compute the pinned bottom row from the rows currently rendered. Returns
 * `undefined` when no column has a summary configured (so AgGrid renders no
 * summary row).
 *
 * Mark for the future: when Enterprise is purchased, REPLACE this with
 * AgGrid's native grandTotalRow + agAggregationComponent. This JS computation
 * works for Community but doesn't track filter changes after the initial
 * computation — see comments in LopCrudPage on how we pass post-filter rows.
 */
export function computeSummaryRow<T extends object>(
  rows: T[],
  settings: UserCustomizationSettings | null,
  baseColumnDefs: ColDef<T>[],
): Record<string, unknown> | null {
  if (!settings) return null;

  // Explicitly-numeric columns, plus columns with NO explicit cellDataType
  // (inferred by AgGrid at runtime) whose first non-null row value is a number.
  // Columns explicitly typed as anything else (text/date/...) never aggregate.
  const numericFields = new Set<string>();
  for (const c of baseColumnDefs) {
    if (!c.field) continue;
    const field = c.field as string;
    if (c.cellDataType === 'number') {
      numericFields.add(field);
    } else if (c.cellDataType === undefined || c.cellDataType === true) {
      for (const r of rows) {
        const v = (r as unknown as Record<string, unknown>)[field];
        if (v === null || v === undefined || v === '') continue;
        if (typeof v === 'number') numericFields.add(field);
        break;
      }
    }
  }

  let any = false;
  const result: Record<string, unknown> = {};

  for (const [field, cust] of Object.entries(settings.columns)) {
    if (!cust.summary) continue;
    if (!numericFields.has(field)) continue;
    any = true;
    result[field] = computeAggregate(rows, field, cust.summary);
  }

  if (!any) return null;
  return result;
}

/** Build the AgGrid column state (order + widths + sort) to push into a grid
 *  from a customization. Returns null when there is nothing to apply.
 *  `leadingColIds` = columns (e.g. the pinned selection column) that must stay
 *  in front when the order is applied.
 *
 *  This puts layout into AgGrid's internal column state instead of into the
 *  colDefs, so the user can still drag/resize freely without a re-render
 *  undoing it. Callers use `api.applyColumnState(result)`. */
export function buildColumnStateFromCustomization(
  settings: UserCustomizationSettings | null,
  leadingColIds: string[] = [],
): { state: ColumnState[]; applyOrder: boolean } | null {
  if (!settings) return null;
  const order = settings.grid.columnOrder ?? [];
  const sortList = settings.grid.sort;
  const sortIndexById = new Map<string, number>();
  sortList?.forEach((s, i) => sortIndexById.set(s.colId, i));

  const ids: string[] = [...order];
  for (const field of Object.keys(settings.columns)) {
    if (!ids.includes(field)) ids.push(field);
  }
  for (const s of sortList ?? []) {
    if (!ids.includes(s.colId)) ids.push(s.colId);
  }

  const state: ColumnState[] = [];
  for (const colId of leadingColIds) state.push({ colId });
  let any = false;
  for (const colId of ids) {
    const entry: ColumnState = { colId };
    const w = settings.columns[colId]?.width;
    if (typeof w === 'number' && w > 0) {
      entry.width = w;
      any = true;
    }
    if (sortList !== undefined) {
      const idx = sortIndexById.get(colId);
      if (idx === undefined) {
        entry.sort = null;
      } else {
        entry.sort = sortList[idx].sort;
        entry.sortIndex = idx;
        any = true;
      }
    }
    state.push(entry);
  }
  const applyOrder = order.length > 0;
  if (!any && !applyOrder) return null;
  return { state, applyOrder };
}

/** Clone `current` (or start empty) and overwrite its layout part (column
 *  order, widths, sort) from a live `api.getColumnState()`. Never mutates
 *  `current`. This is what every save/quick-action/dialog-open must go through,
 *  so a persisted or re-applied customization never drags the grid back to a
 *  stale order. Widths of still-flexing columns (never user-resized) are NOT
 *  stored, so those columns keep filling the grid. */
export function snapshotLiveLayout(
  current: UserCustomizationSettings | null,
  state: ColumnState[],
): UserCustomizationSettings {
  const next: UserCustomizationSettings = current
    ? (JSON.parse(JSON.stringify(current)) as UserCustomizationSettings)
    : emptyCustomization();
  const order: string[] = [];
  const sorted: Array<{ colId: string; sort: 'asc' | 'desc'; idx: number }> = [];
  for (const s of state) {
    // The pinned selection column is not a data column.
    if (s.colId === 'ag-Grid-SelectionColumn') continue;
    order.push(s.colId);
    if (typeof s.width === 'number' && s.flex == null) {
      next.columns[s.colId] = { ...(next.columns[s.colId] ?? {}), width: s.width };
    } else if (next.columns[s.colId]?.width != null) {
      next.columns[s.colId] = { ...next.columns[s.colId], width: null };
    }
    if (s.sort === 'asc' || s.sort === 'desc') {
      sorted.push({ colId: s.colId, sort: s.sort, idx: s.sortIndex ?? 0 });
    }
  }
  next.grid.columnOrder = order;
  next.grid.sort = sorted
    .sort((a, b) => a.idx - b.idx)
    .map(({ colId, sort }) => ({ colId, sort }));
  return next;
}

/** Build a rowClassRules entry for zebra striping when enabled. Returns null
 *  if not enabled. The caller merges into its rowClassRules object. */
export function buildZebraRule(
  settings: UserCustomizationSettings | null,
): Record<string, (p: { node: { rowIndex: number | null } }) => boolean> | null {
  if (!settings?.grid.zebraStriping) return null;
  return {
    'lop-crud-row-zebra': (p) =>
      typeof p.node.rowIndex === 'number' && p.node.rowIndex % 2 === 1,
  };
}

/** Clone `current` (or start empty) and merge `patch` into `columns[colId]`.
 *  Used by the right-click "quick format" shortcut so every consumer builds the
 *  next customization the same way. Never mutates `current`. */
export function withColumnPatch(
  current: UserCustomizationSettings | null,
  colId: string,
  patch: Partial<ColumnCustomization>,
): UserCustomizationSettings {
  const next: UserCustomizationSettings = current
    ? (JSON.parse(JSON.stringify(current)) as UserCustomizationSettings)
    : emptyCustomization();
  next.columns[colId] = { ...(next.columns[colId] ?? {}), ...patch };
  return next;
}

// ---------- Internals ----------

function layerColumnCustomization<T>(
  base: ColDef<T>,
  cust: ColumnCustomization,
): ColDef<T> {
  const next: ColDef<T> = { ...base };

  // NOTE: width is intentionally NOT mirrored into colDef here. Doing so
  // forces AgGrid to reset the column to the customization-provided width on
  // every render that recomputes colDefs — which then fights the user's live
  // drag-resize on the grid. Width is owned by AgGrid's internal column state
  // and applied ONCE on grid-ready via `applyColumnStateFromCustomization`
  // (see hooks/grid usage in LopCrudPage.handleGridReady).

  // Hidden — toggle AgGrid's hide flag. The column still exists in the model
  // and can be re-shown via the customization dialog.
  if (cust.hidden) {
    next.hide = true;
  }

  // valueFormatter: compose so the existing one (if any) runs first, then our
  // format overrides if present. Most LopCrudPage columns DON'T set a
  // valueFormatter (cellDataType handles it), so this mostly just adds one.
  if (cust.format) {
    const prior = base.valueFormatter;
    next.valueFormatter = (params: ValueFormatterParams<T>) => {
      const value =
        prior && typeof prior === 'function'
          ? (prior(params) as unknown)
          : params.value;
      return applyFormat(value, cust.format);
    };
  }

  // cellStyle: merge static styles from the customization. Need a function
  // when sign-coloring is on (value-dependent).
  const staticStyle = buildStaticStyle(cust);
  if (cust.signColoring) {
    // Value-based: many columns have no explicit cellDataType (AgGrid infers
    // it at runtime), so we color whenever the cell value is a finite number.
    const explicitNumeric = base.cellDataType === 'number';
    next.cellStyle = (params: CellClassParams<T>) => {
      return buildCellStyleForValue(cust, params.value, explicitNumeric);
    };
  } else if (Object.keys(staticStyle).length > 0) {
    next.cellStyle = staticStyle as CellStyle;
  }

  return next;
}

/** Static styles only — color/bgcolor/font/bold/italic/textAlign. Pure function. */
export function buildStaticStyle(cust: ColumnCustomization): CellStyle {
  const s: CellStyle = {};
  if (cust.color) s.color = cust.color;
  if (cust.backgroundColor) s.backgroundColor = cust.backgroundColor;
  if (cust.fontFamily) s.fontFamily = cust.fontFamily;
  if (cust.bold) s.fontWeight = 'bold';
  if (cust.italic) s.fontStyle = 'italic';
  if (cust.textAlign) s.textAlign = cust.textAlign;
  return s;
}

/** Full effective cell style for a specific value — accounts for sign-coloring.
 *  Used by both the live AgGrid cellStyle and the preview box in the dialog. */
export function buildCellStyleForValue(
  cust: ColumnCustomization,
  value: unknown,
  isNumericCol: boolean,
): CellStyle {
  const staticStyle = buildStaticStyle(cust);
  // Numeric = the column is declared numeric OR the value itself is a number
  // (inferred-type columns carry no cellDataType on the base ColDef).
  if (cust.signColoring && (isNumericCol || typeof value === 'number')) {
    const v = Number(value);
    if (Number.isFinite(v)) {
      if (v > 0) return { ...staticStyle, color: 'var(--color-positive)' };
      if (v < 0) return { ...staticStyle, color: 'var(--color-negative)' };
      // zero: fall through — keep static color (or column default).
    }
  }
  return staticStyle;
}

function computeAggregate<T>(
  rows: T[],
  field: string,
  summary: NonNullable<ColumnCustomization['summary']>,
): number | null {
  const values: number[] = [];
  for (const r of rows) {
    const raw = (r as unknown as Record<string, unknown>)[field];
    if (raw === null || raw === undefined || raw === '') continue;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isFinite(n)) values.push(n);
  }
  if (values.length === 0) return null;

  switch (summary) {
    case 'sum':   return values.reduce((a, b) => a + b, 0);
    case 'avg':   return values.reduce((a, b) => a + b, 0) / values.length;
    case 'count': return values.length;
    case 'min':   return Math.min(...values);
    case 'max':   return Math.max(...values);
    default:      return null;
  }
}
